/**
 * Tracking route — reads the route param and hands off to TrackingResultPanel.
 *
 * Defensive guard: if the URL was hand-crafted with an invalid tracking
 * number we render the "not found" empty state directly rather than letting
 * the API return 404 (saves a round-trip and avoids leaking enumeration
 * signal upstream).
 */
import type { JSX } from 'react';
import { Link, useParams } from 'react-router-dom';

import { TrackingLookupForm } from '../features/track/TrackingLookupForm';
import { TrackingResultPanel } from '../features/track/TrackingResultPanel';
import { isValidTrackingNumber, normaliseTrackingNumber } from '../features/track/upu-s10';

export function TrackingRoute(): JSX.Element {
  const params = useParams<{ trackingNumber: string }>();
  const raw = params.trackingNumber ?? '';
  const normalised = normaliseTrackingNumber(raw);
  const valid = isValidTrackingNumber(normalised);

  return (
    <section className="space-y-8">
      <header className="flex flex-col gap-2">
        <p className="text-sm">
          <Link
            to="/"
            className="text-nipost-700 hover:underline"
          >
            &larr; Look up another parcel
          </Link>
        </p>
        <h1 className="text-2xl font-bold text-slate-900">
          Tracking <span className="font-mono">{normalised}</span>
        </h1>
      </header>

      {valid ? (
        <TrackingResultPanel trackingNumber={normalised} />
      ) : (
        <div className="space-y-4 rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <p className="text-sm text-slate-700">
            <span className="font-mono">{normalised}</span> isn&apos;t a valid
            NIPOST tracking number. Try entering it again below.
          </p>
          <TrackingLookupForm />
        </div>
      )}
    </section>
  );
}
